import { useState, useEffect } from 'react';
import { validateEmail, validatePhone, validateDoc, PHONE_EXTENSIONS, DOC_TYPES } from '../utils/fieldTypes';

const API = process.env.REACT_APP_API_URL || 'https://aldealix-backend-258966414953.europe-west1.run.app';

const CURRENT_YEAR = new Date().getFullYear();
const TODAY = new Date().toISOString().split('T')[0];
const YESTERDAY = new Date(Date.now() - 86400000).toISOString().split('T')[0];

function getAge(yearOfBirth) {
  if (!yearOfBirth || yearOfBirth < 1900) return null;
  return CURRENT_YEAR - yearOfBirth;
}

// ── Evalúa si una condición se cumple ──────────────────────────
function evaluateCondition(condition, formValues) {
  if (!condition || !condition.fieldId) return true;
  const fieldValue = formValues?.[condition.fieldId];
  switch (condition.operator) {
    case 'equals':
      return String(fieldValue ?? '') === String(condition.value ?? '');
    case 'not_equals':
      return String(fieldValue ?? '') !== String(condition.value ?? '');
    case 'contains':
      return String(fieldValue ?? '').toLowerCase().includes(String(condition.value ?? '').toLowerCase());
    case 'not_empty':
      return fieldValue !== undefined && fieldValue !== null && fieldValue !== '' && fieldValue !== false;
    case 'is_empty':
      return !fieldValue || fieldValue === '' || fieldValue === false;
    default:
      return true;
  }
}

// ── Renderiza un campo personalizado individual ──────────────
function CustomField({ field, value, onChange, errors, setErr, clearErr, token }) {
  const val = value?.[field.id];
  const set = (v) => onChange({ ...value, [field.id]: v });
  const fieldId = field.id || field.label;

  if (field.type === 'textarea') {
    return <textarea className="form-textarea" value={val || ''} onChange={e => set(e.target.value)} placeholder={field.placeholder} />;
  }
  if (field.type === 'select') {
    return (
      <select className="form-select" value={val || ''} onChange={e => set(e.target.value)}>
        <option value="">— Selecciona —</option>
        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === 'boolean') {
    return (
      <label className="toggle-wrap">
        <input type="checkbox" checked={!!val} onChange={e => set(e.target.checked)} />
        <span className="toggle-track"><span className="toggle-thumb" /></span>
        <span style={{ fontSize: '.9rem', color: 'var(--mid)' }}>{val ? 'Sí' : 'No'}</span>
      </label>
    );
  }
  if (field.type === 'file') {
    const fileData = val ? (typeof val === 'object' ? val : { name: val }) : null;
    return (
      <div>
        <input
          className="form-input"
          type="file"
          style={{ padding: '.5rem .7rem', cursor: 'pointer' }}
          onChange={async e => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (file.size > 10 * 1024 * 1024) {
              alert('El archivo no puede superar 10MB.');
              e.target.value = '';
              return;
            }
            set({ name: file.name, status: 'uploading', path: null });
            try {
              const metaRes = await fetch(`${API}/api/uploads/signed-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                  filename: file.name,
                  contentType: file.type || 'application/octet-stream',
                  fieldId: field.id,
                }),
              });
              const { uploadUrl, fields: formFields, path } = await metaRes.json();
              const formData = new FormData();
              Object.entries(formFields).forEach(([k, v]) => formData.append(k, v));
              formData.append('file', file);
              await fetch(uploadUrl, { method: 'POST', body: formData });
              set({ name: file.name, status: 'done', path });
            } catch {
              set({ name: file.name, status: 'error', path: null });
            }
          }}
        />
        {fileData && (
          <div className="form-hint" style={{
            color: fileData.status === 'done' ? 'var(--sage)' :
                   fileData.status === 'error' ? 'var(--danger)' : 'var(--amber)'
          }}>
            {fileData.status === 'uploading' && '⏳ Subiendo...'}
            {fileData.status === 'done' && `✅ ${fileData.name}`}
            {fileData.status === 'error' && `❌ Error al subir ${fileData.name}`}
            {!fileData.status && `📎 ${fileData.name}`}
          </div>
        )}
      </div>
    );
  }
  if (field.type === 'phone') {
    const extKey = `${fieldId}_ext`;
    const ext = value?.[extKey] || '+34';
    const errKey = `custom_${fieldId}`;
    return (
      <div>
        <div className="phone-row">
          <select className="form-select phone-ext" value={ext}
            onChange={e => onChange({ ...value, [extKey]: e.target.value })}>
            {PHONE_EXTENSIONS.map(p => <option key={p.value} value={p.value}>{p.flag} {p.value}</option>)}
          </select>
          <input
            className={`form-input${errors?.[errKey] ? ' input-error' : ''}`}
            type="tel"
            value={val || ''}
            placeholder="612 345 678"
            onChange={e => { set(e.target.value); clearErr(errKey); }}
            onBlur={e => {
              if (e.target.value && !validatePhone(e.target.value, ext))
                setErr(errKey, `Número inválido para ${ext}`);
            }}
          />
        </div>
        {errors?.[errKey] && <div className="form-error">{errors[errKey]}</div>}
        <div className="form-hint">Para España (+34): 9 dígitos empezando por 6, 7 o 9</div>
      </div>
    );
  }
  return (
    <input
      className="form-input"
      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'email' ? 'email' : 'text'}
      value={val || ''}
      onChange={e => set(e.target.value)}
      placeholder={field.placeholder}
    />
  );
}

// ── Agrupa campos por paquete de origen ──────────────────────
function groupFieldsByPack(fields, packList = []) {
  const packs = {};
  const loose = [];

  // Mapa id -> nombre de todos los paquetes cargados
  const packNames = {};
  packList.forEach(p => { if (p.id) packNames[p.id] = p.name; });

  fields.forEach(f => {
    if (f.fromPack) {
      // Prioridad: packName en el campo > nombre de packList > null (se resolverá async)
      const resolvedName = f.packName || packNames[f.fromPack] || null;
      if (!packs[f.fromPack]) packs[f.fromPack] = { id: f.fromPack, name: resolvedName, fields: [] };
      else if (!packs[f.fromPack].name && resolvedName) packs[f.fromPack].name = resolvedName;
      packs[f.fromPack].fields.push(f);
    } else {
      loose.push(f);
    }
  });

  return { packs: Object.values(packs), loose };
}

export default function DynamicForm({ fields = [], value, onChange, prefix = '', packs = [], token = '', excludedMandatory = [] }) {
  const mandExcluded = (key) => Array.isArray(excludedMandatory) && excludedMandatory.includes(key);
  const [errors, setErrors] = useState({});
  const [resolvedPacks, setResolvedPacks] = useState(packs);

  // Si hay campos con fromPack sin nombre resuelto, cargar paquetes desde API
  useEffect(() => {
    if (packs.length > 0) { setResolvedPacks(packs); return; }
    const hasUnresolved = fields.some(f => f.fromPack && !f.packName);
    if (!hasUnresolved) return;
    fetch(
      (process.env.REACT_APP_API_URL || 'https://aldealix-backend-258966414953.europe-west1.run.app')
      + '/api/admin/question-packs',
      { headers: { 'Content-Type': 'application/json' } }
    )
      .then(r => r.json())
      .then(d => setResolvedPacks(d.packs || []))
      .catch(() => {});
  }, [fields, packs]);

  const set = (key, val) => onChange({ ...value, [key]: val });
  const setErr = (key, msg) => setErrors(p => ({ ...p, [key]: msg }));
  const clearErr = (key) => setErrors(p => { const n = { ...p }; delete n[key]; return n; });

  const birthYear = parseInt(value?.birthYear) || null;
  const age = getAge(birthYear);
  const needsDoc = age !== null && age >= 16;
  const needsTutor = age !== null && age < 18;

  // Filtrar campos cuya condición no se cumple
  const visibleFields = fields.filter(f => evaluateCondition(f.condition, value));

  const { packs: fieldPacks, loose } = groupFieldsByPack(visibleFields, resolvedPacks);

  const renderField = (field) => (
    <div className="form-group" key={field.id || field.label}>
      <label className="form-label">
        {field.label} {field.required && <span className="req">*</span>}
      </label>
      <CustomField
        field={field} value={value} onChange={onChange}
        errors={errors} setErr={setErr} clearErr={clearErr} token={token}
      />
    </div>
  );

  return (
    <div>
      {/* Nombre */}
      <div className="form-row-3">
        <div className="form-group">
          <label className="form-label">Nombre <span className="req">*</span></label>
          <input className="form-input" value={value?.firstName || ''} onChange={e => set('firstName', e.target.value)} placeholder="Nombre" />
        </div>
        <div className="form-group">
          <label className="form-label">Primer apellido <span className="req">*</span></label>
          <input className="form-input" value={value?.lastName1 || ''} onChange={e => set('lastName1', e.target.value)} placeholder="Apellido 1" />
        </div>
        {!mandExcluded('field:Segundo apellido') && (
          <div className="form-group">
            <label className="form-label">Segundo apellido <span className="req">*</span></label>
            <input className="form-input" value={value?.lastName2 || ''} onChange={e => set('lastName2', e.target.value)} placeholder="Apellido 2" />
          </div>
        )}
      </div>

      {/* Año + sexo */}
      <div className="form-row-2">
        <div className="form-group">
          <label className="form-label">Año de nacimiento <span className="req">*</span></label>
          <input
            className={`form-input${errors.birthYear ? ' input-error' : ''}`}
            type="number" min="1900" max={CURRENT_YEAR}
            value={value?.birthYear || ''}
            onChange={e => { set('birthYear', e.target.value); clearErr('birthYear'); }}
            placeholder={CURRENT_YEAR - 30}
          />
          {errors.birthYear && <div className="form-error">{errors.birthYear}</div>}
          {age !== null && age < 6 && <div className="form-hint" style={{ color: 'var(--danger)' }}>Edad mínima: 6 años</div>}
        </div>
        {!mandExcluded('field:Sexo') && (
          <div className="form-group">
            <label className="form-label">Sexo (según documento) <span className="req">*</span></label>
            <select className="form-select" value={value?.sex || ''} onChange={e => set('sex', e.target.value)}>
              <option value="">— Selecciona —</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
            </select>
          </div>
        )}
      </div>

      {/* Contacto */}
      <div className="form-section">{needsTutor ? 'Contacto del tutor legal' : 'Contacto'}</div>
      {needsTutor && (
        <div className="alert alert-warn" style={{ marginBottom: '.8rem' }}>
          <span>⚠</span> Menor de 18 años — se solicitan los datos de contacto del tutor legal
        </div>
      )}
      {!mandExcluded('field:Email') && (
        <div className="form-group">
          <label className="form-label">{needsTutor ? 'Email del tutor legal' : 'Email'} <span className="req">*</span></label>
          <input
            className={`form-input${errors.email ? ' input-error' : ''}`}
            type="email" value={value?.email || ''} placeholder="nombre@email.com"
            onChange={e => { set('email', e.target.value); clearErr('email'); }}
            onBlur={e => { if (e.target.value && !validateEmail(e.target.value)) setErr('email', 'El email debe contener @ y al menos un punto tras el @'); }}
          />
          {errors.email && <div className="form-error">{errors.email}</div>}
        </div>
      )}
      {!mandExcluded('field:Teléfono') && (
        <div className="form-group">
          <label className="form-label">{needsTutor ? 'Teléfono del tutor legal' : 'Teléfono'} <span className="req">*</span></label>
          <div className="phone-row">
            <select className="form-select phone-ext" value={value?.phoneExt || '+34'} onChange={e => { set('phoneExt', e.target.value); clearErr('phone'); }}>
              {PHONE_EXTENSIONS.map(p => <option key={p.value} value={p.value}>{p.flag} {p.value}</option>)}
            </select>
            <input
              className={`form-input${errors.phone ? ' input-error' : ''}`}
              type="tel" value={value?.phone || ''} placeholder="612 345 678"
              onChange={e => { set('phone', e.target.value); clearErr('phone'); }}
              onBlur={e => { const ext = value?.phoneExt || '+34'; if (e.target.value && !validatePhone(e.target.value, ext)) setErr('phone', `Número inválido para ${ext}`); }}
            />
          </div>
          {errors.phone && <div className="form-error">{errors.phone}</div>}
          <div className="form-hint">Para España (+34): 9 dígitos empezando por 6, 7 o 9</div>
        </div>
      )}
      {needsTutor && (
        <>
          {!mandExcluded('field:Nombre tutor legal') && (
            <div className="form-group">
              <label className="form-label">Nombre completo del tutor legal <span className="req">*</span></label>
              <input className="form-input" value={value?.tutorName || ''} onChange={e => set('tutorName', e.target.value)} placeholder="Nombre y apellidos del tutor" />
            </div>
          )}
          {!mandExcluded('field:Documento tutor') && (
            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Tipo doc. tutor <span className="req">*</span></label>
                <select className="form-select" value={value?.tutorDocType || ''}
                  onChange={e => { set('tutorDocType', e.target.value); clearErr('tutorDocNum'); }}>
                  <option value="">— Selecciona —</option>
                  {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Nº documento tutor <span className="req">*</span></label>
                <input
                  className={`form-input${errors.tutorDocNum ? ' input-error' : ''}`}
                  value={value?.tutorDocNum || ''}
                  placeholder={DOC_TYPES.find(d => d.value === value?.tutorDocType)?.hint || 'Número'}
                  onChange={e => { set('tutorDocNum', e.target.value.toUpperCase()); clearErr('tutorDocNum'); }}
                  onBlur={e => {
                    if (e.target.value && value?.tutorDocType && !validateDoc(e.target.value, value.tutorDocType))
                      setErr('tutorDocNum', DOC_TYPES.find(d => d.value === value.tutorDocType)?.hint || 'Formato inválido');
                  }}
                />
                {errors.tutorDocNum && <div className="form-error">{errors.tutorDocNum}</div>}
              </div>
            </div>
          )}
        </>
      )}

      {/* Domicilio */}
      {!mandExcluded('section:Domicilio') && (
        <>
          <div className="form-section">Domicilio</div>
          {!mandExcluded('field:Dirección') && (
            <div className="form-group">
              <label className="form-label">Dirección completa <span className="req">*</span></label>
              <input className="form-input" value={value?.address || ''} onChange={e => set('address', e.target.value)} placeholder="Calle, número, piso..." />
            </div>
          )}
          {(!mandExcluded('field:Municipio') || !mandExcluded('field:Provincia')) && (
            <div className="form-row-2">
              {!mandExcluded('field:Municipio') && (
                <div className="form-group">
                  <label className="form-label">Municipio <span className="req">*</span></label>
                  <input className="form-input" value={value?.municipality || ''} onChange={e => set('municipality', e.target.value)} placeholder="Madrid" />
                </div>
              )}
              {!mandExcluded('field:Provincia') && (
                <div className="form-group">
                  <label className="form-label">Provincia <span className="req">*</span></label>
                  <input className="form-input" value={value?.province || ''} onChange={e => set('province', e.target.value)} placeholder="Madrid" />
                </div>
              )}
            </div>
          )}
          {!mandExcluded('field:País') && (
            <div className="form-group">
              <label className="form-label">País de procedencia <span className="req">*</span></label>
              <select className="form-select" value={value?.country || ''} onChange={e => set('country', e.target.value)}>
                <option value="">— Selecciona país —</option>
                {['España','Portugal','Francia','Alemania','Reino Unido','Italia','Otro'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
        </>
      )}

      {/* Documento */}
      {!mandExcluded('section:Documento de identidad') && needsDoc ? (
        <>
          <div className="form-section">Documento de identidad</div>
          {(!mandExcluded('field:Tipo de documento') || !mandExcluded('field:Número documento')) && (
            <div className="form-row-2">
              {!mandExcluded('field:Tipo de documento') && (
                <div className="form-group">
                  <label className="form-label">Tipo de documento <span className="req">*</span></label>
                  <select className="form-select" value={value?.docType || ''} onChange={e => { set('docType', e.target.value); clearErr('docNum'); }}>
                    <option value="">— Selecciona —</option>
                    {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
              )}
              {!mandExcluded('field:Número documento') && (
                <div className="form-group">
                  <label className="form-label">Número de documento <span className="req">*</span></label>
                  <input
                    className={`form-input${errors.docNum ? ' input-error' : ''}`}
                    value={value?.docNum || ''}
                    placeholder={DOC_TYPES.find(d => d.value === value?.docType)?.hint || 'Número'}
                    onChange={e => { set('docNum', e.target.value.toUpperCase()); clearErr('docNum'); }}
                    onBlur={e => { if (e.target.value && value?.docType && !validateDoc(e.target.value, value.docType)) setErr('docNum', DOC_TYPES.find(d => d.value === value.docType)?.hint || 'Formato inválido'); }}
                  />
                  {errors.docNum && <div className="form-error">{errors.docNum}</div>}
                </div>
              )}
            </div>
          )}
          {!mandExcluded('field:Soporte documento') && (value?.docType === 'dni' || value?.docType === 'nie') && (
            <div className="form-group">
              <label className="form-label">Número de soporte (dorso del DNI/NIE)</label>
              <input className="form-input" value={value?.docSupport || ''} onChange={e => set('docSupport', e.target.value.toUpperCase())} placeholder="AAA123456" />
              <div className="form-hint">Número de 9 caracteres en el dorso del documento</div>
            </div>
          )}
          {!mandExcluded('field:Fecha emisión documento') && (
            <div className="form-group">
              <label className="form-label">Fecha de emisión del documento <span className="req">*</span></label>
              <input
                className={`form-input${errors.docEmission ? ' input-error' : ''}`}
                type="date" max={YESTERDAY} value={value?.docEmission || ''}
                onChange={e => { set('docEmission', e.target.value); clearErr('docEmission'); }}
                onBlur={e => { if (e.target.value >= TODAY) setErr('docEmission', 'La fecha de emisión no puede ser hoy ni futura'); }}
              />
              {errors.docEmission && <div className="form-error">{errors.docEmission}</div>}
            </div>
          )}
        </>
      ) : age !== null && age < 16 ? (
        <div className="alert alert-info" style={{ marginTop: '.8rem' }}>
          <span>ℹ️</span> Menor de 16 años — no es necesario el documento de identidad
        </div>
      ) : null}

      {/* ── Información de salud (obligatorio) ── */}
      {!mandExcluded('section:Información de salud') && (
        <>
          <div className="form-section">Información de salud</div>
          {!mandExcluded('field:Alergias') && (
            <div className="form-group">
              <label className="form-label">Alergias <span className="req">*</span></label>
              <textarea className="form-textarea" value={value?.alergias || ''} onChange={e => set('alergias', e.target.value)}
                placeholder="Alimentarias, medicamentosas, ambientales... Indicar 'Ninguna' si no tiene" />
            </div>
          )}
          {!mandExcluded('field:Medicación actual') && (
            <div className="form-group">
              <label className="form-label">Medicación actual</label>
              <textarea className="form-textarea" value={value?.medicacionActual || ''} onChange={e => set('medicacionActual', e.target.value)}
                placeholder="Medicación que toma actualmente y pauta. Dejar en blanco si no toma ninguna" />
            </div>
          )}
          {!mandExcluded('field:Enfermedades') && (
            <div className="form-group">
              <label className="form-label">Enfermedades o condiciones crónicas</label>
              <textarea className="form-textarea" value={value?.enfermedades || ''} onChange={e => set('enfermedades', e.target.value)}
                placeholder="Asma, diabetes, epilepsia, etc. Dejar en blanco si no tiene" />
            </div>
          )}
          {(!mandExcluded('field:Grupo sanguíneo') || !mandExcluded('field:Tarjeta sanitaria')) && (
            <div className="form-row-2">
              {!mandExcluded('field:Grupo sanguíneo') && (
                <div className="form-group">
                  <label className="form-label">Grupo sanguíneo</label>
                  <select className="form-select" value={value?.grupoSanguineo || ''} onChange={e => set('grupoSanguineo', e.target.value)}>
                    <option value="">— Selecciona —</option>
                    {['A+','A-','B+','B-','AB+','AB-','O+','O-','No lo sé'].map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
              )}
              {!mandExcluded('field:Tarjeta sanitaria') && (
                <div className="form-group">
                  <label className="form-label">Nº tarjeta sanitaria</label>
                  <input className="form-input" value={value?.tarjetaSanitaria || ''} onChange={e => set('tarjetaSanitaria', e.target.value)}
                    placeholder="Número de la tarjeta sanitaria" />
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Contacto de emergencia */}
      {!mandExcluded('section:Contacto de emergencia') && (
        <>
          <div className="form-section">Contacto de emergencia</div>
          {(!mandExcluded('field:Nombre emergencia') || !mandExcluded('field:Relación emergencia')) && (
            <div className="form-row-2">
              {!mandExcluded('field:Nombre emergencia') && (
                <div className="form-group">
                  <label className="form-label">Nombre completo <span className="req">*</span></label>
                  <input className="form-input" value={value?.contactoEmergenciaNombre || ''} onChange={e => set('contactoEmergenciaNombre', e.target.value)}
                    placeholder="Nombre y apellidos" />
                </div>
              )}
              {!mandExcluded('field:Relación emergencia') && (
                <div className="form-group">
                  <label className="form-label">Relación <span className="req">*</span></label>
                  <select className="form-select" value={value?.contactoEmergenciaRelacion || ''} onChange={e => set('contactoEmergenciaRelacion', e.target.value)}>
                    <option value="">— Selecciona —</option>
                    {['Padre','Madre','Tutor/a legal','Abuelo/a','Hermano/a','Tío/a','Otro'].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}
          {!mandExcluded('field:Teléfono emergencia') && (
            <div className="form-group">
              <label className="form-label">Teléfono de emergencia <span className="req">*</span></label>
              <div className="phone-row">
                <select className="form-select phone-ext" value={value?.contactoEmergenciaExt || '+34'}
                  onChange={e => set('contactoEmergenciaExt', e.target.value)}>
                  {PHONE_EXTENSIONS.map(p => <option key={p.value} value={p.value}>{p.flag} {p.value}</option>)}
                </select>
                <input
                  className={`form-input${errors?.emergPhone ? ' input-error' : ''}`}
                  type="tel" value={value?.contactoEmergenciaTelefono || ''} placeholder="612 345 678"
                  onChange={e => { set('contactoEmergenciaTelefono', e.target.value); clearErr('emergPhone'); }}
                  onBlur={e => {
                    const ext = value?.contactoEmergenciaExt || '+34';
                    if (e.target.value && !validatePhone(e.target.value, ext)) setErr('emergPhone', `Número inválido para ${ext}`);
                  }}
                />
              </div>
              {errors?.emergPhone && <div className="form-error">{errors.emergPhone}</div>}
            </div>
          )}
        </>
      )}

      {/* Autorizaciones */}
      <div className="form-section">Autorizaciones</div>

      {!mandExcluded('field:Autoriza medicación') && (
        <div className="form-group" style={{ marginTop: '.3rem' }}>
          <label className="form-label">Autorización de medicación <span className="req">*</span></label>
          <label className="toggle-wrap">
            <input type="checkbox" checked={!!value?.autorizaMedicacion} onChange={e => set('autorizaMedicacion', e.target.checked)} />
            <span className="toggle-track"><span className="toggle-thumb" /></span>
            <span style={{ fontSize: '.9rem', color: 'var(--mid)' }}>
              {value?.autorizaMedicacion ? 'Autorizo la administración de medicamentos en caso necesario' : 'No autorizo'}
            </span>
          </label>
          <div className="form-hint">Autorizo al equipo a administrar la medicación indicada y/o medicamentos básicos (paracetamol, ibuprofeno) en caso de necesidad</div>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Cesión de datos personales <span className="req">*</span></label>
        <label className="toggle-wrap">
          <input type="checkbox" checked={!!value?.autorizaCesionDatos} onChange={e => set('autorizaCesionDatos', e.target.checked)} />
          <span className="toggle-track"><span className="toggle-thumb" /></span>
          <span style={{ fontSize: '.9rem', color: value?.autorizaCesionDatos ? 'var(--sage)' : 'var(--danger)' }}>
            {value?.autorizaCesionDatos ? 'Autorizo la cesión de datos personales' : 'No autorizo (obligatorio)'}
          </span>
        </label>
        <div className="form-hint">Autorizo el tratamiento de los datos personales proporcionados conforme a la normativa vigente (RGPD). Este consentimiento es obligatorio para realizar la inscripción.</div>
        {!value?.autorizaCesionDatos && (
          <div className="form-error" style={{ marginTop: '.3rem' }}>Es obligatorio autorizar la cesión de datos para poder inscribirse</div>
        )}
      </div>

      {!mandExcluded('field:Uso de imagen en redes sociales') && (
        <div className="form-group">
          <label className="form-label">Uso de imagen en redes sociales</label>
          <label className="toggle-wrap">
            <input type="checkbox" checked={!!value?.autorizaImagenRedes} onChange={e => set('autorizaImagenRedes', e.target.checked)} />
            <span className="toggle-track"><span className="toggle-thumb" /></span>
            <span style={{ fontSize: '.9rem', color: 'var(--mid)' }}>
              {value?.autorizaImagenRedes ? 'Autorizo el uso de mi imagen' : 'No autorizo'}
            </span>
          </label>
          <div className="form-hint">Autorizo el uso de fotografías y vídeos tomados durante la actividad para su publicación en redes sociales y web de la organización</div>
        </div>
      )}

      {!mandExcluded('field:Comunicaciones comerciales') && (
        <div className="form-group">
          <label className="form-label">Comunicaciones comerciales</label>
          <label className="toggle-wrap">
            <input type="checkbox" checked={!!value?.autorizaComunicaciones} onChange={e => set('autorizaComunicaciones', e.target.checked)} />
            <span className="toggle-track"><span className="toggle-thumb" /></span>
            <span style={{ fontSize: '.9rem', color: 'var(--mid)' }}>
              {value?.autorizaComunicaciones ? 'Autorizo el envío de comunicaciones' : 'No autorizo'}
            </span>
          </label>
          <div className="form-hint">Autorizo el envío de información sobre futuras actividades, eventos y promociones por email</div>
        </div>
      )}

      {/* Campos adicionales agrupados por paquete */}
      {visibleFields.length > 0 && (
        <>
          {/* Secciones por paquete */}
          {fieldPacks.map((pack, pi) => (
            <div key={pack.name + pi}>
              <div className="form-section">{pack.name || '📦 Preguntas adicionales'}</div>
              {pack.fields.map(renderField)}
            </div>
          ))}

          {/* Campos sueltos sin paquete */}
          {loose.length > 0 && (
            <>
              <div className="form-section">{packs.length > 0 ? 'Otras preguntas' : 'Campos adicionales del evento'}</div>
              {loose.map(renderField)}
            </>
          )}
        </>
      )}
    </div>
  );
}
